import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Tooltip,
  Skeleton,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Alert
} from '@mui/material';
import { Table } from 'antd';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BuildIcon from '@mui/icons-material/Build';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, addWeeks } from 'date-fns';
import { es as esLocale, it as itLocale, enUS as enLocale } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/apiError';
import { API_BASE_URL, createAuthHeader } from '../../config/api.config';
import CandidateDropdown from '../ProblemsTable/CandidateDropdown';
import InlineDatePicker from '../ProblemsTable/InlineDatePicker';
import { inlineUpdateExperiment, bulkAcceptExperiments, getProblemsGroupDetails } from '../../data/problemsApi';
import AppliedPricingDialog from '../shared/AppliedPricingDialog';

// Shared typography styles
const sharedFontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';
const sharedLetterSpacing = '-0.01em';

// Format numbers for compact display (e.g., 42100 -> 42.1K)
const formatCompactNumber = (value) => {
  if (!value || isNaN(value)) return value;
  const num = parseFloat(value);
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  return num.toString();
};

// Tipo de movimiento de la fila. Sin esta marca una alza y un descuento se leen igual (mismo
// "producto → algo") y el operador no sabe si está subiendo o bajando el precio.
const EXPERIMENT_TYPE_BADGE = {
  alza_precio_sugerido: { symbol: '↑', color: '#2e7d32', bg: '#EDF7ED', labelKey: 'priceIncrease', fallback: 'Alza de precio' },
  promo_precio_unitario: { symbol: '↓', color: '#1976d2', bg: '#E8F1FC', labelKey: 'discount', fallback: 'Descuento' },
  reemplazo_producto: { symbol: '⇄', color: '#6B7280', bg: '#F3F4F6', labelKey: 'replacement', fallback: 'Reemplazo' },
  ampliar_carril: { symbol: '➕', color: '#3730a3', bg: '#eef2ff', labelKey: 'laneExpansion', fallback: 'Ampliar Carril' },
};

// Get warm color for metric status
const getWarmColor = (status) => {
  switch (status) {
    case 'danger': return '#D32F2F';
    case 'warning': return '#F57C00';
    case 'good': return '#388E3C';
    case 'normal':
    default: return '#757575';
  }
};

const AggregatedExperimentsModal = ({
  open,
  onClose,
  aggregationType = 'cluster', // 'cluster' | 'sector' | 'route'
  aggregationId,  // Generic: works for cluster, sector, and route
  aggregationName, // Generic: works for cluster, sector, and route
  // Backward compatibility props (deprecated, use generic ones)
  clusterId,
  clusterName,
  productId = null,
  productName = null,
  clientId,
  onExperimentsUpdated,
  // Route-specific props (routes are temporal)
  month = null,
  year = null,
  // Alert counts from parent (to know if we need to fetch alerts)
  problemCount = 0,
  alertCount = 0
}) => {
  const { t, i18n } = useTranslation();
  const dateFnsLocale = { es: esLocale, en: enLocale, it: itLocale }[i18n.language] || esLocale;
  const [loading, setLoading] = useState(false);
  const [experiments, setExperiments] = useState([]);
  const [experimentDrafts, setExperimentDrafts] = useState({});
  const [acceptingAll, setAcceptingAll] = useState(false);
  const [acceptingId, setAcceptingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);

  // Alerts state
  const [alerts, setAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  // Alert state machine: { alertId: { state: 'pending'|'broken'|'under_review'|'confirm_experiment'|'experiment', plannedDate: Date, experimentData: Object } }
  const [alertStates, setAlertStates] = useState({});
  const [alertProcessingId, setAlertProcessingId] = useState(null);
  const [alertExperimentDrafts, setAlertExperimentDrafts] = useState({});
  const [acceptError, setAcceptError] = useState('');
  const [priceReview, setPriceReview] = useState(null);

  // Determine if we should show tabs (only when BOTH exist)
  const hasBothTypes = problemCount > 0 && alertCount > 0;
  // If only alerts exist, start on alerts tab
  const initialTab = problemCount === 0 && alertCount > 0 ? 1 : 0;

  // Resolve the ID and name from either generic or legacy props
  const resolvedId = aggregationId || clusterId;
  const resolvedName = aggregationName || clusterName;

  // Reset tab selection when modal opens
  useEffect(() => {
    if (open) {
      setSelectedTab(initialTab);
    }
  }, [open, initialTab]);

  // Fetch experiments when modal opens
  useEffect(() => {
    if (open && resolvedId && clientId && problemCount > 0) {
      fetchExperiments();
    }
  }, [open, resolvedId, clientId, productId, aggregationType, problemCount]);

  // Fetch alerts when modal opens (if there are alerts)
  useEffect(() => {
    if (open && resolvedId && clientId && alertCount > 0) {
      fetchAlerts();
    }
  }, [open, resolvedId, clientId, productId, aggregationType, alertCount]);

  const fetchExperiments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');

      // Step 1: Get experiment IDs based on aggregation type
      let basePath;
      if (aggregationType === 'cluster') {
        basePath = `/store-clusters/${encodeURIComponent(resolvedId)}/experiments`;
      } else if (aggregationType === 'sector') {
        basePath = `/sectors/${encodeURIComponent(resolvedId)}/experiments`;
      } else {
        // route
        basePath = `/routes/${encodeURIComponent(resolvedId)}/experiments`;
      }
      let url = `${API_BASE_URL}${basePath}?clientId=${encodeURIComponent(clientId)}`;
      if (productId) {
        url += `&productId=${productId}`;
      }
      // Route requires month/year params
      if (aggregationType === 'route' && month && year) {
        url += `&month=${month}&year=${year}`;
      }

      const idsResponse = await axios.get(url, { headers: createAuthHeader(token) });
      const experimentIds = idsResponse.data.experimentIds || [];

      if (experimentIds.length === 0) {
        setExperiments([]);
        setLoading(false);
        return;
      }

      // Step 2: Get experiment details using the existing API
      const details = await getProblemsGroupDetails(
        clientId,
        experimentIds,
        { sortField: 'opportunityCost', sortDirection: 'desc' }
      );

      setExperiments(details || []);
    } catch (error) {
      console.error(`Error fetching ${aggregationType} experiments:`, error);
      setExperiments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    setLoadingAlerts(true);
    try {
      const token = localStorage.getItem('access_token');

      // Build URL based on aggregation type
      let basePath;
      if (aggregationType === 'cluster') {
        basePath = `/store-clusters/${encodeURIComponent(resolvedId)}/alerts`;
      } else if (aggregationType === 'sector') {
        basePath = `/sectors/${encodeURIComponent(resolvedId)}/alerts`;
      } else {
        // route
        basePath = `/routes/${encodeURIComponent(resolvedId)}/alerts`;
      }
      let url = `${API_BASE_URL}${basePath}?clientId=${encodeURIComponent(clientId)}`;
      if (productId) {
        url += `&productId=${productId}`;
      }
      // Route requires month/year params
      if (aggregationType === 'route' && month && year) {
        url += `&month=${month}&year=${year}`;
      }

      const response = await axios.get(url, { headers: createAuthHeader(token) });
      const alertsData = response.data.alerts || [];
      setAlerts(alertsData);

      // Initialize alert states based on their current status
      const initialStates = {};
      alertsData.forEach(alert => {
        if (alert.status === 'under_review') {
          initialStates[alert.id] = {
            state: 'under_review',
            plannedDate: alert.resolutionDate ? new Date(alert.resolutionDate) : null
          };
        } else {
          initialStates[alert.id] = {
            state: 'pending',
            plannedDate: null,
            experimentData: null
          };
        }
      });
      setAlertStates(initialStates);
    } catch (error) {
      console.error(`Error fetching ${aggregationType} alerts:`, error);
      setAlerts([]);
      setAlertStates({});
    } finally {
      setLoadingAlerts(false);
    }
  };

  // Handle "Yes, it's broken" click - go to date picker state
  const handleConfirmBroken = useCallback((alertId) => {
    setAlertStates(prev => ({
      ...prev,
      [alertId]: {
        ...prev[alertId],
        state: 'broken',
        plannedDate: addWeeks(new Date(), 1) // Default to 1 week from now
      }
    }));
  }, []);

  // Handle "No, it's not broken" click - go to confirm_experiment state
  const handleConfirmNotBroken = useCallback((alertId) => {
    setAlertStates(prev => ({
      ...prev,
      [alertId]: {
        ...prev[alertId],
        state: 'confirm_experiment'
      }
    }));
  }, []);

  // Handle date change for broken alerts
  const handleAlertDateChange = useCallback((alertId, date) => {
    setAlertStates(prev => ({
      ...prev,
      [alertId]: {
        ...prev[alertId],
        plannedDate: date
      }
    }));
  }, []);

  // Reset alert state back to pending
  const handleResetAlertState = useCallback((alertId) => {
    setAlertStates(prev => ({
      ...prev,
      [alertId]: { ...prev[alertId], state: 'pending', experimentData: null }
    }));
  }, []);

  // Submit broken confirmation with date - keep in table with under_review state
  const handleSubmitBroken = useCallback(async (alertId) => {
    setAlertProcessingId(alertId);
    try {
      const token = localStorage.getItem('access_token');
      const state = alertStates[alertId];
      const formattedDate = state.plannedDate ? format(state.plannedDate, 'yyyy-MM-dd') : null;

      await axios.post(
        `${API_BASE_URL}/alerts/update?clientId=${encodeURIComponent(clientId)}`,
        {
          alertIds: [alertId],
          action: 'confirm_broken',
          metadata: { planned_resolution_date: formattedDate }
        },
        { headers: createAuthHeader(token) }
      );

      // Transition to under_review state - keep in table
      setAlertStates(prev => ({
        ...prev,
        [alertId]: {
          ...prev[alertId],
          state: 'under_review',
          plannedDate: state.plannedDate
        }
      }));

      onExperimentsUpdated?.();
    } catch (error) {
      console.error('Error confirming broken:', error);
    } finally {
      setAlertProcessingId(null);
    }
  }, [alertStates, clientId, onExperimentsUpdated]);

  // Mark as repaired - final dismissal
  const handleMarkRepaired = useCallback(async (alertId) => {
    setAlertProcessingId(alertId);
    try {
      const token = localStorage.getItem('access_token');
      const state = alertStates[alertId];

      await axios.post(
        `${API_BASE_URL}/alerts/update?clientId=${encodeURIComponent(clientId)}`,
        {
          alertIds: [alertId],
          action: 'set_resolution_date',
          metadata: {
            resolution_date: format(state.plannedDate || new Date(), 'yyyy-MM-dd')
          }
        },
        { headers: createAuthHeader(token) }
      );

      // Remove alert from list
      setAlerts(prev => prev.filter(a => a.id !== alertId));
      setAlertStates(prev => {
        const newStates = { ...prev };
        delete newStates[alertId];
        return newStates;
      });

      if (alerts.length <= 1) {
        onExperimentsUpdated?.();
        onClose();
      }
    } catch (error) {
      console.error('Error marking repaired:', error);
    } finally {
      setAlertProcessingId(null);
    }
  }, [alertStates, alerts.length, clientId, onExperimentsUpdated, onClose]);

  // Handle "No" to create experiment - just dismiss the alert
  const handleDismissAlert = useCallback(async (alertId) => {
    setAlertProcessingId(alertId);
    try {
      const token = localStorage.getItem('access_token');

      await axios.post(
        `${API_BASE_URL}/alerts/update?clientId=${encodeURIComponent(clientId)}`,
        {
          alertIds: [alertId],
          action: 'confirm_not_broken'
        },
        { headers: createAuthHeader(token) }
      );

      // Remove alert from list
      setAlerts(prev => prev.filter(a => a.id !== alertId));
      setAlertStates(prev => {
        const newStates = { ...prev };
        delete newStates[alertId];
        return newStates;
      });

      if (alerts.length <= 1) {
        onExperimentsUpdated?.();
        onClose();
      }
    } catch (error) {
      console.error('Error dismissing alert:', error);
    } finally {
      setAlertProcessingId(null);
    }
  }, [alerts.length, clientId, onExperimentsUpdated, onClose]);

  // Handle "Yes" to create experiment - fetch AI recommendation and transition to experiment state
  const handleCreateExperiment = useCallback(async (alertId) => {
    setAlertProcessingId(alertId);
    try {
      const token = localStorage.getItem('access_token');
      const alert = alerts.find(a => a.id === alertId);
      if (!alert) return;

      // Fetch AI candidate suggestion for replacement experiment
      const response = await axios.get(
        `${API_BASE_URL}/experiments/candidate-suggestion`,
        {
          params: {
            clientId,
            storeId: alert.storeId,
            productId: alert.productId,
            experimentType: 'reemplazo_producto'
          },
          headers: createAuthHeader(token)
        }
      );

      const aiSuggestion = response.data;
      const candidates = aiSuggestion?.candidates || [];
      const firstCandidate = candidates[0];

      // Build experiment data for the row
      const experimentData = {
        experimentType: 'reemplazo_producto',
        candidateProductIds: candidates.map(c => c.dProductId || c.productId),
        candidateNames: candidates.map(c => c.productName),
        selectedCandidateId: firstCandidate?.dProductId || firstCandidate?.productId || null,
        discount: 0,
        dateFrom: format(addWeeks(new Date(), 1), 'yyyy-MM-dd'),
        dateTo: format(addWeeks(new Date(), 5), 'yyyy-MM-dd'),
        aiSuggestion: aiSuggestion,
        storeId: alert.storeId,
        productId: alert.productId
      };

      setAlertStates(prev => ({
        ...prev,
        [alertId]: {
          ...prev[alertId],
          state: 'experiment',
          experimentData
        }
      }));
    } catch (error) {
      console.error('Error fetching AI recommendation:', error);
      // Still transition to experiment state but without AI data
      const alert = alerts.find(a => a.id === alertId);
      setAlertStates(prev => ({
        ...prev,
        [alertId]: {
          ...prev[alertId],
          state: 'experiment',
          experimentData: {
            experimentType: 'reemplazo_producto',
            candidateProductIds: [],
            candidateNames: [],
            selectedCandidateId: null,
            discount: 0,
            dateFrom: format(addWeeks(new Date(), 1), 'yyyy-MM-dd'),
            dateTo: format(addWeeks(new Date(), 5), 'yyyy-MM-dd'),
            storeId: alert?.storeId,
            productId: alert?.productId
          }
        }
      }));
    } finally {
      setAlertProcessingId(null);
    }
  }, [alerts, clientId]);

  // Handle alert experiment draft changes
  const handleAlertExperimentDraftChange = useCallback((alertId, changes) => {
    setAlertExperimentDrafts(prev => ({
      ...prev,
      [alertId]: {
        ...prev[alertId],
        ...changes,
        hasChanges: true
      }
    }));
  }, []);

  // Accept experiment from alert
  const handleAcceptAlertExperiment = useCallback(async (alertId, pricingOverride = null) => {
    setAlertProcessingId(alertId);
    setAcceptError('');
    try {
      const token = localStorage.getItem('access_token');
      const state = alertStates[alertId];
      const draft = alertExperimentDrafts[alertId] || {};
      const expData = state?.experimentData || {};
      const alert = alerts.find(a => a.id === alertId);

      // Create first: an ERP rejection must leave the alert visible and actionable.
      const experimentData = {
        dStoreId: expData.storeId || alert?.storeId,
        dProductId: expData.productId || alert?.productId,
        experimentType: expData.experimentType || 'reemplazo_producto',
        discount: draft.discount ?? expData.discount ?? 0,
        newDProductId: draft.candidateProductId || expData.selectedCandidateId,
        dateFrom: draft.dateFrom || expData.dateFrom,
        dateTo: draft.dateTo || expData.dateTo,
        status: 'accepted',
        alertId: alertId,
        isAi: Boolean(expData.aiSuggestion)
      };

      if (!pricingOverride) {
        const preview = await axios.post(
          `${API_BASE_URL}/experiments/pricing-preview?clientId=${encodeURIComponent(clientId)}`,
          experimentData,
          { headers: createAuthHeader(token) }
        );
        if (preview.data?.pricing) {
          setPriceReview({ alertId, pricing: preview.data.pricing });
          return;
        }
      }
      if (pricingOverride) experimentData.pricingOverride = pricingOverride;

      await axios.post(
        `${API_BASE_URL}/experiments/create?clientId=${encodeURIComponent(clientId)}`,
        experimentData,
        { headers: createAuthHeader(token) }
      );
      await axios.post(
        `${API_BASE_URL}/alerts/update?clientId=${encodeURIComponent(clientId)}`,
        { alertIds: [alertId], action: 'confirm_not_broken' },
        { headers: createAuthHeader(token) }
      );

      // Remove alert from list
      setAlerts(prev => prev.filter(a => a.id !== alertId));
      setAlertStates(prev => {
        const newStates = { ...prev };
        delete newStates[alertId];
        return newStates;
      });
      setAlertExperimentDrafts(prev => {
        const newDrafts = { ...prev };
        delete newDrafts[alertId];
        return newDrafts;
      });

      onExperimentsUpdated?.();
      // Antes se dejaba abierto cuando había que mostrar lo aplicado por el ERP; sin esa
      // confirmación, la última alerta cierra el modal directamente.
      if (alerts.length <= 1) onClose();
    } catch (error) {
      console.error('Error accepting alert experiment:', error);
      setAcceptError(getApiErrorMessage(error, 'No se pudo aceptar el experimento.'));
    } finally {
      setAlertProcessingId(null);
    }
  }, [alertStates, alertExperimentDrafts, alerts, clientId, onExperimentsUpdated, onClose]);

  // Reject experiment from alert - just dismiss
  const handleRejectAlertExperiment = useCallback(async (alertId) => {
    setAlertProcessingId(alertId);
    try {
      const token = localStorage.getItem('access_token');

      await axios.post(
        `${API_BASE_URL}/alerts/update?clientId=${encodeURIComponent(clientId)}`,
        {
          alertIds: [alertId],
          action: 'confirm_not_broken'
        },
        { headers: createAuthHeader(token) }
      );

      // Remove alert from list
      setAlerts(prev => prev.filter(a => a.id !== alertId));
      setAlertStates(prev => {
        const newStates = { ...prev };
        delete newStates[alertId];
        return newStates;
      });

      if (alerts.length <= 1) {
        onExperimentsUpdated?.();
        onClose();
      }
    } catch (error) {
      console.error('Error rejecting alert experiment:', error);
    } finally {
      setAlertProcessingId(null);
    }
  }, [alerts.length, clientId, onExperimentsUpdated, onClose]);

  // Handle draft changes (date, candidate, discount)
  const handleDraftChange = useCallback((experimentId, changes) => {
    setExperimentDrafts(prev => ({
      ...prev,
      [experimentId]: {
        ...prev[experimentId],
        ...changes,
        hasChanges: true
      }
    }));
  }, []);

  // Accept a single experiment. Aceptar agenda los precios contra el ERP (inline-update llama al
  // gateway antes de escribir nada), así que primero se enseña el diálogo de tenders con el
  // precio exacto que se va a enviar y el operador confirma o lo corrige.
  const handleAcceptExperiment = useCallback(async (experimentId, pricingOverride = null) => {
    setAcceptingId(experimentId);
    setAcceptError('');
    try {
      const draft = experimentDrafts[experimentId] || {};
      const exp = experiments.find(e => e.experimentId === experimentId);
      const updates = {
        status: 'accepted',
        ...(draft.dateFrom && { dateFrom: draft.dateFrom }),
        ...(draft.dateTo && { dateTo: draft.dateTo }),
        ...(draft.candidateProductId && { candidateProductId: draft.candidateProductId }),
        ...(draft.discount !== undefined && { discount: draft.discount }),
        // Include price info for fallback price test order creation
        ...(exp?.originalPrice && { originalPrice: exp.originalPrice }),
        ...(exp?.isFallbackPrice && { isFallbackPrice: exp.isFallbackPrice }),
      };

      if (!pricingOverride) {
        const token = localStorage.getItem('access_token');
        const preview = await axios.post(
          `${API_BASE_URL}/experiments/pricing-preview?clientId=${encodeURIComponent(clientId)}`,
          {
            experimentId,
            ...updates,
            // El preview no conoce `candidateProductId`: si el operador cambió el reemplazo en
            // el desplegable hay que decirle qué producto entra para que cotice ese.
            ...(draft.candidateProductId && { newDProductId: draft.candidateProductId }),
          },
          { headers: createAuthHeader(token) }
        );
        if (preview.data?.pricing) {
          setPriceReview({ experimentId, pricing: preview.data.pricing });
          return;
        }
      }
      if (pricingOverride) updates.pricingOverride = pricingOverride;

      // El diálogo editable ya enseñó (y dejó corregir) los precios exactos que se envían, así
      // que no se repite un recap de sólo lectura después de aceptar.
      await inlineUpdateExperiment(clientId, experimentId, updates);

      // Remove from local state
      setExperiments(prev => prev.filter(exp => exp.experimentId !== experimentId));

      // Clear draft
      setExperimentDrafts(prev => {
        const newDrafts = { ...prev };
        delete newDrafts[experimentId];
        return newDrafts;
      });

      // If no more experiments, close modal
      if (experiments.length <= 1) {
        onExperimentsUpdated?.();
        onClose();
      }
    } catch (error) {
      console.error('Error accepting experiment:', error);
      setAcceptError(getApiErrorMessage(error, 'No se pudo aceptar el experimento.'));
    } finally {
      setAcceptingId(null);
    }
  }, [clientId, experimentDrafts, experiments, onClose, onExperimentsUpdated]);

  // Reject a single experiment
  const handleRejectExperiment = useCallback(async (experimentId) => {
    setRejectingId(experimentId);
    try {
      await inlineUpdateExperiment(clientId, experimentId, { status: 'rejected' });

      // Remove from local state
      setExperiments(prev => prev.filter(exp => exp.experimentId !== experimentId));

      // Clear draft
      setExperimentDrafts(prev => {
        const newDrafts = { ...prev };
        delete newDrafts[experimentId];
        return newDrafts;
      });

      // If no more experiments, close modal
      if (experiments.length <= 1) {
        onExperimentsUpdated?.();
        onClose();
      }
    } catch (error) {
      console.error('Error rejecting experiment:', error);
    } finally {
      setRejectingId(null);
    }
  }, [clientId, experiments.length, onClose, onExperimentsUpdated]);

  // Accept all experiments
  const handleAcceptAll = useCallback(async () => {
    setAcceptingAll(true);
    setAcceptError('');
    try {
      const experimentIds = experiments.map(exp => exp.experimentId);

      // Apply date changes individually (dates require separate updates)
      for (const exp of experiments) {
        const draft = experimentDrafts[exp.experimentId];
        if (draft?.hasChanges && (draft.dateFrom || draft.dateTo)) {
          const updates = {
            ...(draft.dateFrom && { dateFrom: draft.dateFrom }),
            ...(draft.dateTo && { dateTo: draft.dateTo }),
          };
          if (Object.keys(updates).length > 0) {
            await inlineUpdateExperiment(clientId, exp.experimentId, updates);
          }
        }
      }

      // Collect experiments with fallback prices for test order creation
      const experimentsWithPrices = experiments
        .filter(exp => exp.isFallbackPrice && exp.originalPrice)
        .map(exp => ({
          experimentId: exp.experimentId,
          originalPrice: exp.originalPrice,
          isFallbackPrice: true
        }));

      // Collect product updates from drafts for bulk processing
      const experimentProductUpdates = experiments
        .filter(exp => {
          const draft = experimentDrafts[exp.experimentId];
          return draft?.candidateProductId || draft?.discount !== undefined;
        })
        .map(exp => {
          const draft = experimentDrafts[exp.experimentId];
          return {
            experimentId: exp.experimentId,
            candidateProductId: draft?.candidateProductId || exp.candidateProductIds?.[0],
            discount: draft?.discount ?? exp.discount
          };
        })
        .filter(update => update.candidateProductId); // Only include if we have a candidate

      // Bulk accept all with product updates
      await bulkAcceptExperiments(clientId, experimentIds, experimentsWithPrices, experimentProductUpdates);

      onExperimentsUpdated?.();
      onClose();
    } catch (error) {
      console.error('Error accepting all experiments:', error);
      // El rechazo del ERP (fecha sin margen, etc.) tiene que verse: si no, el operador cree
      // que se aceptaron y no ha pasado nada.
      setAcceptError(getApiErrorMessage(error, 'No se pudieron aceptar los experimentos.'));
    } finally {
      setAcceptingAll(false);
    }
  }, [clientId, experiments, experimentDrafts, onClose, onExperimentsUpdated]);

  // Create tooltip content for AI summary - matches Diagnostics pattern
  const createAITooltipContent = (record) => {
    const oppCost = record.opportunityCost;
    const hasOppCost = oppCost !== null && oppCost !== undefined && oppCost !== 0;
    const metrics = record.context?.metrics;
    const hasMetrics = metrics && metrics.length >= 3;
    const [current, benchmark, market] = hasMetrics ? metrics : [{}, {}, {}];

    return (
      <Box sx={{
        padding: '12px',
        minWidth: '280px',
        maxWidth: '340px',
        fontFamily: 'SF Pro, Inter, system-ui',
        fontSize: '11px'
      }}>
        {/* Opportunity Cost Section */}
        {hasOppCost && (
          <Box sx={{ mb: 1.5, pb: 1.5, borderBottom: '1px solid #E2E8F0' }}>
            <Typography sx={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#E57373',
              mb: 0.5,
              letterSpacing: '-0.01em'
            }}>
              €{Math.abs(oppCost).toFixed(0)} {t('lost', 'perdido')}
            </Typography>
            <Typography sx={{
              fontSize: '10px',
              color: '#718096',
              fontStyle: 'italic',
              lineHeight: 1.3
            }}>
              {t('oppCostCaveat', 'Coste de oportunidad proyectado')}
            </Typography>
          </Box>
        )}

        {/* Performance Metrics Section */}
        {hasMetrics && (
          <>
            <Typography sx={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#2D3748',
              mb: 1.5,
              letterSpacing: '-0.01em'
            }}>
              {t('product_performance', 'Rendimiento del Producto')}
            </Typography>

            {/* Current Sales */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
              <Typography sx={{
                fontSize: '14px',
                color: getWarmColor(current.status),
                mt: -0.2
              }}>
                ↓
              </Typography>
              <Box>
                <Typography sx={{
                  fontSize: '11px',
                  fontWeight: 500,
                  color: '#2D3748',
                  lineHeight: 1.2
                }}>
                  {formatCompactNumber(current.value)} {t('units', 'unidades')}
                </Typography>
                <Typography sx={{
                  fontSize: '10px',
                  color: '#718096',
                  lineHeight: 1.2
                }}>
                  {t('currentProductPerformance', 'Rendimiento actual del producto')} ({t('sixWeeks', '6 semanas')})
                </Typography>
              </Box>
            </Box>

            {/* Category Benchmark */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
              <Typography sx={{
                fontSize: '14px',
                color: '#388E3C',
                mt: -0.2
              }}>
                △
              </Typography>
              <Box>
                <Typography sx={{
                  fontSize: '11px',
                  fontWeight: 500,
                  color: '#2D3748',
                  lineHeight: 1.2
                }}>
                  {formatCompactNumber(benchmark.value)} {t('unitsIn', 'unidades en')} {benchmark.detail?.category || t('category', 'categoría')}
                  {benchmark.detail?.sector ? ` (${benchmark.detail.sector})` : ''}
                </Typography>
                <Typography sx={{
                  fontSize: '10px',
                  color: '#718096',
                  lineHeight: 1.2
                }}>
                  {t('categoryAverageInSector', 'Promedio de categoría en el sector')} ({t('sixWeeks', '6 semanas')})
                </Typography>
              </Box>
            </Box>

            {/* Market Activity */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
              <Typography sx={{
                fontSize: '14px',
                color: '#388E3C',
                mt: -0.2
              }}>
                △
              </Typography>
              <Box>
                <Typography sx={{
                  fontSize: '11px',
                  fontWeight: 500,
                  color: '#2D3748',
                  lineHeight: 1.2
                }}>
                  {formatCompactNumber(market.value)} {t('units', 'unidades')}
                </Typography>
                <Typography sx={{
                  fontSize: '10px',
                  color: '#718096',
                  lineHeight: 1.2
                }}>
                  {t('locationTotalInArea', 'Total de la ubicación en el área')} ({t('sixWeeks', '6 semanas')})
                </Typography>
              </Box>
            </Box>
          </>
        )}

        {/* AI Recommendation / Description */}
        {record.description && (
          <Box sx={{
            pt: hasMetrics ? 1.5 : 0,
            borderTop: hasMetrics ? '1px solid #E2E8F0' : 'none'
          }}>
            <Typography sx={{
              fontSize: '11px',
              color: '#4A5568',
              lineHeight: 1.5,
              fontStyle: 'italic'
            }}>
              {record.description}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  // Table columns
  const columns = [
    // Machine column
    {
      title: t('machine', 'Machine'),
      dataIndex: 'machineAlias',
      key: 'machine',
      width: 60,
      ellipsis: true,
      render: (alias, record) => (
        <Tooltip title={alias || record.machineId || 'Unknown'}>
          <Typography sx={{
            fontSize: '11px',
            fontFamily: sharedFontFamily,
            letterSpacing: sharedLetterSpacing,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {alias || record.machineId || 'Unknown'}
          </Typography>
        </Tooltip>
      ),
    },
    // Candidate column (includes original product name with arrow)
    {
      title: t('candidate', 'Candidate'),
      key: 'candidate',
      width: 300,
      render: (_, record) => {
        const badge = EXPERIMENT_TYPE_BADGE[record.experimentType];
        return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          {badge && (
            <Tooltip title={t(badge.labelKey, badge.fallback)}>
              <Box
                component="span"
                sx={{
                  flexShrink: 0,
                  width: 18,
                  height: 18,
                  borderRadius: '4px',
                  backgroundColor: badge.bg,
                  color: badge.color,
                  fontSize: '11px',
                  fontWeight: 700,
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'help',
                }}
              >
                {badge.symbol}
              </Box>
            </Tooltip>
          )}
          <Tooltip title={record.originalProductName || productName || 'Unknown'}>
            <Typography sx={{
              fontSize: '11px',
              fontFamily: sharedFontFamily,
              letterSpacing: sharedLetterSpacing,
              color: '#6B7280',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flexShrink: 1,
              minWidth: 0,
              maxWidth: '120px',
            }}>
              {record.originalProductName || productName || 'Unknown'}
            </Typography>
          </Tooltip>
          <Typography sx={{ fontSize: '11px', color: '#9CA3AF', flexShrink: 0 }}>→</Typography>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <CandidateDropdown
              experimentId={record.experimentId}
              experimentType={record.experimentType}
              selectedCandidateId={record.selectedCandidateId}
              candidateProductIds={record.candidateProductIds || []}
              candidateNames={record.candidateNames || []}
              candidateDiscounts={record.candidateDiscounts || []}
              discount={record.discount}
              // Sin markup/targetPrice la fila de una alza cae en la rama sin datos del
              // desplegable y no enseña el par de precios (€p0 → €p1 +X%).
              markup={record.markup}
              targetPrice={record.targetPrice}
              onDraftChange={handleDraftChange}
              clientId={clientId}
              originalPrice={record.originalPrice}
              isFallbackPrice={record.isFallbackPrice}
            />
          </Box>
        </Box>
        );
      },
    },
    // Dates column
    {
      title: t('dates', 'Dates'),
      key: 'dates',
      width: 90,
      align: 'center',
      render: (_, record) => (
        <InlineDatePicker
          experimentId={record.experimentId}
          dateFrom={record.dateFrom}
          dateTo={record.dateTo}
          onDraftChange={handleDraftChange}
          t={t}
        />
      ),
    },
    // Actions column
    {
      title: '',
      key: 'actions',
      width: 110,
      align: 'center',
      fixed: 'right',
      render: (_, record) => {
        const hasDraft = experimentDrafts[record.experimentId]?.hasChanges;
        const isAccepting = acceptingId === record.experimentId;
        const isRejecting = rejectingId === record.experimentId;
        const isProcessing = isAccepting || isRejecting;

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            {hasDraft && (
              <Box sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: '#f59e0b',
                flexShrink: 0,
              }} />
            )}
            {/* AI Icon with tooltip */}
            <Tooltip
              title={createAITooltipContent(record)}
              arrow
              placement="left"
              enterDelay={200}
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: '#FFFFFF',
                    color: '#1F2937',
                    borderRadius: '8px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                    border: '1px solid #E5E7EB',
                    borderLeft: '4px solid #7C3AED',
                    p: 0,
                    maxWidth: 'none'
                  }
                },
                arrow: {
                  sx: {
                    color: '#FFFFFF',
                    '&:before': {
                      border: '1px solid #E5E7EB'
                    }
                  }
                }
              }}
            >
              <IconButton
                size="small"
                sx={{
                  background: 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)',
                  color: '#7C3AED',
                  width: 24,
                  height: 24,
                  border: '1px solid #C4B5FD',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #DDD6FE 0%, #C4B5FD 100%)',
                  },
                }}
              >
                <AutoAwesomeIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
            {/* Accept button */}
            <Tooltip title={t('accept', 'Accept')}>
              <IconButton
                size="small"
                onClick={() => handleAcceptExperiment(record.experimentId)}
                disabled={isProcessing}
                sx={{
                  color: '#10B981',
                  '&:hover': { backgroundColor: '#D1FAE5' }
                }}
              >
                {isAccepting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CheckIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Tooltip>
            {/* Reject button */}
            <Tooltip title={t('reject', 'Reject')}>
              <IconButton
                size="small"
                onClick={() => handleRejectExperiment(record.experimentId)}
                disabled={isProcessing}
                sx={{
                  color: '#EF4444',
                  '&:hover': { backgroundColor: '#FEE2E2' }
                }}
              >
                {isRejecting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <ClearIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  // Build dynamic title based on what's shown
  const getDialogTitle = () => {
    if (hasBothTypes) {
      // When both exist, just show product name (tabs show counts)
      return productName || t('product', 'Producto');
    } else if (problemCount > 0) {
      // Only problems
      return `${experiments.length} ${t('experiments', 'Experimentos')}${productName ? ` - ${productName}` : ''}`;
    } else {
      // Only alerts
      return `${alerts.length} ${t('alerts', 'Alertas')}${productName ? ` - ${productName}` : ''}`;
    }
  };

  // Render problems table content
  const renderProblemsContent = () => {
    if (loading) {
      return (
        <Box sx={{ p: 2 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} height={40} sx={{ my: 1 }} />
          ))}
        </Box>
      );
    }

    if (experiments.length === 0) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography sx={{ color: 'text.secondary' }}>
            {t('noExperimentsPending', 'No hay experimentos pendientes para este producto')}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{
        p: 2,
        '& .ant-table': {
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          overflow: 'hidden',
        },
        '& .ant-table-thead > tr > th': {
          backgroundColor: '#FFFFFF !important',
          borderBottom: '1px solid #E5E7EB',
          color: '#6B7280',
          fontSize: '11px',
          fontWeight: 500,
        },
        '& .ant-table-cell-fix-right': {
          backgroundColor: '#FFFFFF !important',
        },
        '& .ant-table-tbody > tr > td': {
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #F3F4F6',
        },
        '& .ant-table-tbody > tr:last-child > td': {
          borderBottom: 'none',
        },
        '& .ant-table-cell-scrollbar': {
          display: 'none !important',
        },
      }}>
        <Table
          columns={columns}
          dataSource={experiments}
          pagination={false}
          size="small"
          rowKey="experimentId"
          scroll={{ y: 350 }}
          tableLayout="fixed"
        />
      </Box>
    );
  };

  // Alert table columns
  const alertColumns = useMemo(() => [
    // Machine column
    {
      title: t('machine', 'Machine'),
      dataIndex: 'machineAlias',
      key: 'machine',
      width: 80,
      ellipsis: true,
      render: (alias, record) => (
        <Tooltip title={alias || record.machineId || record.storeId || 'Unknown'}>
          <Typography sx={{
            fontSize: '11px',
            fontFamily: sharedFontFamily,
            letterSpacing: sharedLetterSpacing,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {alias || record.machineId || record.storeId || 'Unknown'}
          </Typography>
        </Tooltip>
      ),
    },
    // Product column
    {
      title: t('product', 'Product'),
      dataIndex: 'productName',
      key: 'product',
      width: 140,
      ellipsis: true,
      render: (name) => (
        <Tooltip title={name || productName || 'Unknown'}>
          <Typography sx={{
            fontSize: '12px',
            fontFamily: sharedFontFamily,
            letterSpacing: sharedLetterSpacing,
            color: '#2563eb',
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {name || productName || 'Unknown'}
          </Typography>
        </Tooltip>
      ),
    },
    // Selection Code column
    {
      title: t('selectionCode', 'Selection'),
      dataIndex: 'selectionCode',
      key: 'selectionCode',
      width: 60,
      align: 'center',
      render: (code) => (
        <Typography sx={{
          fontSize: '11px',
          fontFamily: sharedFontFamily,
          color: '#6B7280'
        }}>
          {code || '-'}
        </Typography>
      ),
    },
    // Weeks Without Sales column
    {
      title: t('weeksWithoutSales', 'Weeks'),
      dataIndex: 'weeks_since',
      key: 'weeks_since',
      width: 60,
      align: 'center',
      render: (weeks) => (
        <Typography sx={{
          fontSize: '11px',
          fontFamily: sharedFontFamily,
          fontWeight: 600,
          color: weeks >= 4 ? '#DC2626' : weeks >= 2 ? '#F59E0B' : '#6B7280'
        }}>
          {weeks || 0}
        </Typography>
      ),
    },
    // Action/Status column - This is where the inline flow happens
    {
      title: t('status', 'Status'),
      key: 'action',
      width: 320,
      render: (_, record) => {
        const state = alertStates[record.id];
        const isProcessing = alertProcessingId === record.id;

        if (!state) return null;

        // State: pending - Show "Is this broken?" buttons
        if (state.state === 'pending') {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '12px', color: '#374151', fontWeight: 500 }}>
                {t('isBroken', '¿Está averiado?')}
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                disabled={isProcessing}
              >
                <ToggleButton
                  value="yes"
                  onClick={() => handleConfirmBroken(record.id)}
                  sx={{
                    fontSize: '11px',
                    px: 1.5,
                    py: 0.25,
                    textTransform: 'none',
                    '&:hover': { backgroundColor: '#FEE2E2', color: '#DC2626' }
                  }}
                >
                  {t('yes', 'Sí')}
                </ToggleButton>
                <ToggleButton
                  value="no"
                  onClick={() => handleConfirmNotBroken(record.id)}
                  sx={{
                    fontSize: '11px',
                    px: 1.5,
                    py: 0.25,
                    textTransform: 'none',
                    '&:hover': { backgroundColor: '#D1FAE5', color: '#059669' }
                  }}
                >
                  {t('no', 'No')}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          );
        }

        // State: broken - Show date picker and confirm
        if (state.state === 'broken') {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BuildIcon sx={{ fontSize: 16, color: '#F59E0B' }} />
              <Typography sx={{ fontSize: '10px', color: '#6B7280', whiteSpace: 'nowrap' }}>
                {t('whenPlanToFix', '¿Cuándo planeas repararlo?')}
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateFnsLocale}>
                <DatePicker
                  value={state.plannedDate}
                  onChange={(date) => handleAlertDateChange(record.id, date)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: {
                        width: 95,
                        '& .MuiInputBase-input': {
                          fontSize: '10px',
                          py: 0.25,
                          px: 0.5
                        },
                        '& .MuiInputBase-root': {
                          height: 26,
                          paddingRight: '4px'
                        },
                        '& .MuiInputAdornment-root': {
                          marginLeft: 0
                        },
                        '& .MuiIconButton-root': {
                          padding: '2px',
                          '& svg': {
                            fontSize: '16px'
                          }
                        }
                      }
                    }
                  }}
                  format="dd/MM/yy"
                  disabled={isProcessing}
                />
              </LocalizationProvider>
              <Button
                size="small"
                variant="contained"
                onClick={() => handleSubmitBroken(record.id)}
                disabled={isProcessing || !state.plannedDate}
                sx={{
                  fontSize: '10px',
                  textTransform: 'none',
                  px: 1,
                  py: 0.25,
                  minWidth: 'auto',
                  backgroundColor: '#F59E0B',
                  '&:hover': { backgroundColor: '#D97706' }
                }}
              >
                {isProcessing ? <CircularProgress size={14} /> : t('confirm', 'Confirmar')}
              </Button>
              <IconButton
                size="small"
                onClick={() => handleResetAlertState(record.id)}
                disabled={isProcessing}
                sx={{ padding: 0.25 }}
              >
                <ClearIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          );
        }

        // State: under_review - Show scheduled date + mark repaired button
        if (state.state === 'under_review') {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BuildIcon sx={{ fontSize: 16, color: '#F59E0B' }} />
              <Typography sx={{ fontSize: '11px', color: '#6B7280' }}>
                {t('scheduledFor', 'Programado:')} {state.plannedDate ? format(state.plannedDate, 'dd/MM/yy') : '-'}
              </Typography>
              <Button
                size="small"
                variant="contained"
                startIcon={<CheckIcon sx={{ fontSize: 14 }} />}
                onClick={() => handleMarkRepaired(record.id)}
                disabled={isProcessing}
                sx={{
                  fontSize: '10px',
                  textTransform: 'none',
                  px: 1,
                  py: 0.25,
                  backgroundColor: '#10B981',
                  '&:hover': { backgroundColor: '#059669' }
                }}
              >
                {isProcessing ? <CircularProgress size={14} /> : t('markRepaired', 'Marcar reparado')}
              </Button>
            </Box>
          );
        }

        // State: confirm_experiment - Ask if user wants to create experiment
        if (state.state === 'confirm_experiment') {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '12px', color: '#374151', fontWeight: 500 }}>
                {t('createExperiment', '¿Crear experimento?')}
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                disabled={isProcessing}
              >
                <ToggleButton
                  value="yes"
                  onClick={() => handleCreateExperiment(record.id)}
                  sx={{
                    fontSize: '11px',
                    px: 1.5,
                    py: 0.25,
                    textTransform: 'none',
                    '&:hover': { backgroundColor: '#D1FAE5', color: '#059669' }
                  }}
                >
                  {isProcessing ? <CircularProgress size={14} /> : t('yes', 'Sí')}
                </ToggleButton>
                <ToggleButton
                  value="no"
                  onClick={() => handleDismissAlert(record.id)}
                  sx={{
                    fontSize: '11px',
                    px: 1.5,
                    py: 0.25,
                    textTransform: 'none',
                    '&:hover': { backgroundColor: '#FEE2E2', color: '#DC2626' }
                  }}
                >
                  {t('no', 'No')}
                </ToggleButton>
              </ToggleButtonGroup>
              <IconButton
                size="small"
                onClick={() => handleResetAlertState(record.id)}
                disabled={isProcessing}
                sx={{ padding: 0.25 }}
              >
                <ClearIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          );
        }

        // State: experiment - Show experiment row like problems table
        if (state.state === 'experiment') {
          const expData = state.experimentData || {};
          const draft = alertExperimentDrafts[record.id] || {};

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
              {/* Candidate dropdown */}
              <Box sx={{ minWidth: 120, maxWidth: 160 }}>
                <CandidateDropdown
                  experimentId={`alert-${record.id}`}
                  experimentType={expData.experimentType || 'reemplazo_producto'}
                  selectedCandidateId={draft.candidateProductId || expData.selectedCandidateId}
                  candidateProductIds={expData.candidateProductIds || []}
                  candidateNames={expData.candidateNames || []}
                  candidateDiscounts={[]}
                  discount={draft.discount ?? expData.discount ?? 0}
                  onDraftChange={(id, changes) => handleAlertExperimentDraftChange(record.id, changes)}
                  clientId={clientId}
                />
              </Box>
              {/* Date pickers */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <InlineDatePicker
                  experimentId={`alert-${record.id}`}
                  dateFrom={draft.dateFrom || expData.dateFrom}
                  dateTo={draft.dateTo || expData.dateTo}
                  onDraftChange={(id, changes) => handleAlertExperimentDraftChange(record.id, changes)}
                  t={t}
                />
              </Box>
              {/* Accept/Reject buttons */}
              <Tooltip title={t('accept', 'Aceptar')}>
                <IconButton
                  size="small"
                  onClick={() => handleAcceptAlertExperiment(record.id)}
                  disabled={isProcessing}
                  sx={{
                    color: '#10B981',
                    '&:hover': { backgroundColor: '#D1FAE5' },
                    padding: 0.5
                  }}
                >
                  {isProcessing ? <CircularProgress size={14} /> : <CheckIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </Tooltip>
              <Tooltip title={t('cancel', 'Cancelar')}>
                <IconButton
                  size="small"
                  onClick={() => handleResetAlertState(record.id)}
                  disabled={isProcessing}
                  sx={{
                    color: '#EF4444',
                    '&:hover': { backgroundColor: '#FEE2E2' },
                    padding: 0.5
                  }}
                >
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        }

        return null;
      },
    },
  ], [
    t, alertStates, alertProcessingId, alertExperimentDrafts,
    handleConfirmBroken, handleConfirmNotBroken, handleAlertDateChange, handleSubmitBroken,
    handleMarkRepaired, handleDismissAlert, handleResetAlertState, handleCreateExperiment,
    handleAlertExperimentDraftChange, handleAcceptAlertExperiment,
    productName, clientId
  ]);

  // Render alerts table content
  const renderAlertsContent = () => {
    if (loadingAlerts) {
      return (
        <Box sx={{ p: 2 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} height={40} sx={{ my: 1 }} />
          ))}
        </Box>
      );
    }

    if (alerts.length === 0) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography sx={{ color: 'text.secondary' }}>
            {t('noAlertsPending', 'No hay alertas pendientes para este producto')}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{
        p: 2,
        '& .ant-table': {
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          overflow: 'hidden',
        },
        '& .ant-table-thead > tr > th': {
          backgroundColor: '#FFFFFF !important',
          borderBottom: '1px solid #E5E7EB',
          color: '#6B7280',
          fontSize: '11px',
          fontWeight: 500,
        },
        '& .ant-table-tbody > tr > td': {
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #F3F4F6',
        },
        '& .ant-table-tbody > tr:last-child > td': {
          borderBottom: 'none',
        },
      }}>
        <Table
          columns={alertColumns}
          dataSource={alerts}
          pagination={false}
          size="small"
          rowKey="id"
          scroll={{ y: 350 }}
          tableLayout="fixed"
        />
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '70vh',
          borderRadius: 2,
        }
      }}
    >
      {/* Misma revisión de tenders para las dos rutas: experimento del orquestador (fila de la
          tabla) y experimento creado desde una alerta. `priceReview` dice cuál la abrió. */}
      <AppliedPricingDialog open={Boolean(priceReview)} pricing={priceReview?.pricing} onClose={() => setPriceReview(null)} onConfirm={(pricingOverride) => { const review = priceReview; setPriceReview(null); if (review.experimentId) handleAcceptExperiment(review.experimentId, pricingOverride); else handleAcceptAlertExperiment(review.alertId, pricingOverride); }} />
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 1.5,
        px: 2,
        borderBottom: hasBothTypes ? 'none' : '1px solid #E5E5E5'
      }}>
        <Box>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            {getDialogTitle()}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
            {resolvedName}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Only show Accept All for problems, and only when there are experiments */}
          {experiments.length > 0 && (selectedTab === 0 || !hasBothTypes) && problemCount > 0 && (
            <Button
              variant="contained"
              size="small"
              onClick={handleAcceptAll}
              disabled={acceptingAll || loading}
              startIcon={acceptingAll ? <CircularProgress size={14} color="inherit" /> : <CheckIcon />}
              sx={{
                backgroundColor: '#10B981',
                '&:hover': { backgroundColor: '#059669' },
                textTransform: 'none',
                fontSize: '0.8rem',
                px: 2
              }}
            >
              {t('acceptAll', 'Aceptar Todo')}
            </Button>
          )}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      {acceptError && <Alert severity="error" sx={{ mx: 2, mt: 1 }}>{acceptError}</Alert>}

      {/* Tabs - only show when BOTH problems AND alerts exist */}
      {hasBothTypes && (
        <Box sx={{ borderBottom: '1px solid #E5E5E5', px: 2 }}>
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            sx={{
              minHeight: 40,
              '& .MuiTabs-indicator': {
                backgroundColor: selectedTab === 0 ? '#6366f1' : '#a855f7',
              },
              '& .MuiTab-root': {
                minHeight: 40,
                textTransform: 'none',
                fontSize: '0.85rem',
                fontWeight: 500,
              },
            }}
          >
            <Tab
              label={`${t('problems', 'Problemas')} (${experiments.length})`}
              sx={{
                color: selectedTab === 0 ? '#6366f1' : '#6B7280',
                '&.Mui-selected': { color: '#6366f1' },
              }}
            />
            <Tab
              label={`${t('alerts', 'Alertas')} (${alerts.length})`}
              sx={{
                color: selectedTab === 1 ? '#a855f7' : '#6B7280',
                '&.Mui-selected': { color: '#a855f7' },
              }}
            />
          </Tabs>
        </Box>
      )}

      <DialogContent sx={{ p: 0 }}>
        {/* Content based on selected tab or single type */}
        {hasBothTypes ? (
          // Tabbed content
          selectedTab === 0 ? renderProblemsContent() : renderAlertsContent()
        ) : problemCount > 0 ? (
          // Only problems
          renderProblemsContent()
        ) : (
          // Only alerts
          renderAlertsContent()
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AggregatedExperimentsModal;
